import Tag16 from '@carbon/icons-react/es/tag/16';
import Timer16 from '@carbon/icons-react/es/timer/16';
import { Dictionary, groupBy, keyBy, mapValues, reduce } from 'lodash';
import moment from 'moment';
import React from 'react';
import { DateType } from 'api/models';
import {
  ProjectFinancialsBarChartApiData,
  ProjectFinancialsContractType,
  ProjectFinancialsDateRange,
  ProjectFinancialsGroupBy,
  ProjectFinancialsZoomLevel,
} from 'api/project';
import { ReactComponent as Recursive16 } from 'assets/svgs/connect-recursive-16.svg';
import { ReactComponent as NonBillable16 } from 'assets/svgs/non-billable-16.svg';
import { defaultFormat } from 'utils/date-field/formatter';
import {
  ProjectFinancialsComponentType,
  ProjectFinancialsTypeZoomLevelMap,
} from '../types';

export const projectFinancialsTypeZoomLevelMap: ProjectFinancialsTypeZoomLevelMap =
  ((): ProjectFinancialsTypeZoomLevelMap => {
    const { Overall, Quarterly, Monthly, Period, Revenue, Profitability } =
      ProjectFinancialsZoomLevel;

    return {
      [ProjectFinancialsContractType.TM]: {
        [ProjectFinancialsComponentType.BudgetLine]: [Revenue, Profitability],
        [ProjectFinancialsComponentType.BudgetBar]: [Quarterly, Monthly],
        [ProjectFinancialsComponentType.HoursBar]: [Quarterly, Monthly],
        [ProjectFinancialsComponentType.BudgetTable]: [
          Overall,
          Quarterly,
          Monthly,
        ],
        [ProjectFinancialsComponentType.HoursTable]: [
          Overall,
          Quarterly,
          Monthly,
        ],
      },
      [ProjectFinancialsContractType.FIXED]: {
        [ProjectFinancialsComponentType.BudgetLine]: [Revenue, Profitability],
        [ProjectFinancialsComponentType.BudgetBar]: [Quarterly, Monthly],
        [ProjectFinancialsComponentType.HoursBar]: [Quarterly, Monthly],
        [ProjectFinancialsComponentType.BudgetTable]: [
          Overall,
          Quarterly,
          Monthly,
        ],
        [ProjectFinancialsComponentType.HoursTable]: [
          Overall,
          Quarterly,
          Monthly,
        ],
      },
      [ProjectFinancialsContractType.SUBSCRIPTION]: {
        [ProjectFinancialsComponentType.BudgetLine]: [Revenue, Profitability],
        [ProjectFinancialsComponentType.BudgetBar]: [Quarterly, Monthly],
        [ProjectFinancialsComponentType.HoursBar]: [Quarterly, Monthly],
        [ProjectFinancialsComponentType.BudgetTable]: [Overall, Period],
        [ProjectFinancialsComponentType.HoursTable]: [Overall, Period],
      },
      [ProjectFinancialsContractType.NONBILLABLE]: {
        [ProjectFinancialsComponentType.BudgetLine]: [],
        [ProjectFinancialsComponentType.BudgetBar]: [Quarterly, Monthly],
        [ProjectFinancialsComponentType.HoursBar]: [Quarterly, Monthly],
        [ProjectFinancialsComponentType.BudgetTable]: [
          Overall,
          Quarterly,
          Monthly,
        ],
        [ProjectFinancialsComponentType.HoursTable]: [
          Overall,
          Quarterly,
          Monthly,
        ],
      },
    };
  })();

export const projectFinancialsGroupByMap = [
  ProjectFinancialsGroupBy.Role,
  ProjectFinancialsGroupBy.Phase,
];

export const projectFinancialsContractTypeToIconMap = {
  [ProjectFinancialsContractType.TM]: <Timer16 />,
  [ProjectFinancialsContractType.FIXED]: <Tag16 />,
  [ProjectFinancialsContractType.SUBSCRIPTION]: <Recursive16 />,
  [ProjectFinancialsContractType.NONBILLABLE]: <NonBillable16 />,
};

export const numberFormatter = (number: number) => Number(number.toFixed(2));

export const hoursToMins = (hours: number): number => hours * 60;
export const minsToHours = (hours: number): number =>
  numberFormatter(hours / 60);

export const percentageRenderer = (value: number | string): string =>
  `${value}%`;

export const getFilteredZoomLevels = (
  zoomLevels: ProjectFinancialsZoomLevel[],
  startDate?: DateType,
  endDate?: DateType,
): ProjectFinancialsZoomLevel[] => {
  if (startDate == null || endDate == null) {
    return [];
  }

  let monthCount = 0;
  let currDate = defaultFormat(moment(startDate).startOf('month'));

  while (moment(currDate).isSameOrBefore(moment(endDate))) {
    monthCount += 1;
    currDate = defaultFormat(moment(currDate).add(1, 'month'));
  }

  return zoomLevels.filter(
    (zoomLevel) =>
      monthCount >= 6 || zoomLevel !== ProjectFinancialsZoomLevel.Quarterly,
  );
};

export const getDateCategoriesBasedOnDuration = (
  { startDate, endDate }: ProjectFinancialsDateRange,
  zoomLevel: ProjectFinancialsZoomLevel,
): DateType[] => {
  const columns: DateType[] = [];

  if (startDate == null || endDate == null) {
    return [];
  }

  if (zoomLevel === ProjectFinancialsZoomLevel.Overall) {
    return [startDate];
  }

  if (zoomLevel === ProjectFinancialsZoomLevel.Period) {
    // TODO: handle periodic columns
    return [];
  }

  let currDate =
    zoomLevel === ProjectFinancialsZoomLevel.Monthly
      ? startDate
      : defaultFormat(moment(startDate).startOf('quarter'));

  while (moment(currDate).isSameOrBefore(moment(endDate))) {
    columns.push(currDate);
    currDate = defaultFormat(
      moment(currDate).add(
        1,
        zoomLevel === ProjectFinancialsZoomLevel.Monthly ? 'month' : 'quarter',
      ),
    );
  }

  return columns;
};

export const getBarChartDataForZoomLevel = (
  barChartData: ProjectFinancialsBarChartApiData[],
  zoomLevel: ProjectFinancialsZoomLevel,
  financialContractType: ProjectFinancialsContractType,
  projectBudget: number,
): Dictionary<ProjectFinancialsBarChartApiData> => {
  const unit =
    zoomLevel === ProjectFinancialsZoomLevel.Quarterly ? 'quarter' : 'month';

  if (zoomLevel === ProjectFinancialsZoomLevel.Quarterly) {
    const dataGroupedByDate = groupBy(barChartData, ({ startDate }) =>
      defaultFormat(moment(startDate).startOf(unit)),
    );

    let prevUnitCost = 0; // this is to be used for FixedFee to store prev quarter's cost when cumulating

    const data = mapValues(dataGroupedByDate, (values, quarter) => {
      let unitCost = 0;

      const groupedValues: ProjectFinancialsBarChartApiData = reduce(
        values,
        (
          result,
          {
            actualRevenue,
            estimatedRevenue,
            estimatedCost,
            actualCost,
            timeAllocated,
            billableTimeTracked,
            nonBillableTimeTracked,
          },
        ) => {
          const temp: Omit<ProjectFinancialsBarChartApiData, 'margin'> = {
            startDate: quarter,
            endDate: defaultFormat(moment(quarter).endOf('quarter')),
            actualRevenue: (result.actualRevenue ?? 0) + (actualRevenue ?? 0),
            estimatedRevenue:
              (result.estimatedRevenue ?? 0) + (estimatedRevenue ?? 0),
            actualCost: (result.actualCost ?? 0) + (actualCost ?? 0),
            estimatedCost: (result.estimatedCost ?? 0) + (estimatedCost ?? 0),
            timeAllocated: (result.timeAllocated ?? 0) + (timeAllocated ?? 0),
            billableTimeTracked:
              (result.billableTimeTracked ?? 0) + (billableTimeTracked ?? 0),
            nonBillableTimeTracked:
              (result.nonBillableTimeTracked ?? 0) +
              (nonBillableTimeTracked ?? 0),
          };

          let margin = 0;

          if (financialContractType === ProjectFinancialsContractType.TM) {
            if (
              (temp.actualCost ?? 0) !== 0 &&
              (temp.actualRevenue ?? 0) !== 0
            ) {
              margin = numberFormatter(
                (1 - (temp.actualCost ?? 0) / (temp.actualRevenue ?? 1)) * 100,
              );
            }
          }

          return {
            ...temp,
            margin,
          };
        },
        {} as ProjectFinancialsBarChartApiData,
      );

      if (financialContractType === ProjectFinancialsContractType.FIXED) {
        unitCost += groupedValues?.actualCost ?? 0;
        prevUnitCost += unitCost;

        if (projectBudget !== 0) {
          groupedValues.margin = numberFormatter(
            (1 - (prevUnitCost ?? 0) / projectBudget) * 100,
          );
        }
      }

      return groupedValues;
    });

    return data;
  }

  return keyBy(barChartData, (data) =>
    defaultFormat(moment(data.startDate).startOf(unit)),
  );
};
